import { useMemo } from "react";
import { useQuery } from "urql";
import { graphql } from "@acto/gql";
import { User } from "@acto/taskpane/core/domain";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";

const ClientTeamQuery = graphql(`
  query ClientTeam($clientId: ID!) {
    clientTeam(clientId: $clientId) {
      id
      name
      __typename
      employee {
        id
        name
        __typename
      }
    }
  }
`);

/**
 * The users a todo on [clientId] may be assigned to: the client's team plus the viewer (anyone
 * with edit rights can take a todo, team member or not), with the viewer never listed twice.
 */
export function useAssignableUsers(clientId?: string): {
  users: User[];
  isFetching: boolean;
  error?: Error;
} {
  const { viewer } = useViewer();

  const [{ data, fetching, error }] = useQuery({
    query: ClientTeamQuery,
    variables: { clientId: clientId ?? "" },
    pause: !clientId,
  });

  const users = useMemo(() => {
    if (!clientId) return [];

    const team = (data?.clientTeam ?? []).map(User.fromGraphql);

    if (!viewer?.displayName) return team;
    if (team.some((user) => user.id === viewer.id)) return team;

    return [viewer, ...team];
  }, [data, clientId, viewer]);

  return { users, isFetching: fetching, error };
}
